import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { arquiteturaProjetos } from '../../shared/data/arquiteturaData';
import { Compass, Layers, CheckCircle2, Clock, Plus, Search, FileSpreadsheet, ShieldCheck } from 'lucide-react';

export const ArchSystem: React.FC<{ prospect: Prospect }> = ({ prospect }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1A1A] font-sans flex flex-col selection:bg-[#1A1A1A] selection:text-white">
      
      {/* Top Navbar Studio ERP - STRICT NO SHADOWS */}
      <header className="h-20 bg-[#1A1A1A] border-b border-stone-800 px-8 flex items-center justify-between shrink-0">
         <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-stone-800 text-[#D4C5B9] flex items-center justify-center font-bold border border-stone-700">
               <Compass className="w-5 h-5" />
            </div>
            <div>
               <h1 className="font-heading font-bold text-lg text-white tracking-wider uppercase">Studio ERP — {prospect.trade_name}</h1>
               <p className="text-[10px] text-stone-400 font-medium">Gestão de Cronogramas & Especificação de Fornecedores</p>
            </div>
         </div>

         <div className="flex items-center gap-4">
            <div className="relative hidden md:block">
               <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
               <input type="text" placeholder="Buscar projeto ou fornecedor..." className="pl-9 pr-4 py-2 bg-stone-800 border border-stone-700 rounded-xl text-xs text-white outline-none w-72 placeholder:text-stone-500" />
            </div>
            <button className="bg-[#C86D51] text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 hover:bg-orange-700 transition-colors border border-[#C86D51]">
               <Plus className="w-4 h-4" /> Novo Projeto Executive
            </button>
         </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 p-8 overflow-y-auto">
         
         {/* Metrics Summary - STRICT NO SHADOWS */}
         <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl border border-stone-300">
               <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest block mb-1">Projetos Executivos em Obra</span>
               <span className="text-3xl font-heading font-bold text-[#1A1A1A]">12</span>
               <p className="text-xs text-emerald-600 font-bold mt-2">✓ 9 Obras no Cronograma</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-stone-300">
               <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest block mb-1">Área Total Projetada</span>
               <span className="text-3xl font-heading font-bold text-[#1A1A1A]">4.850 m²</span>
               <p className="text-xs text-stone-500 font-medium mt-2">Ticket Médio R$ 4.2k/m²</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-stone-300">
               <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest block mb-1">Fornecedores Homologados</span>
               <span className="text-3xl font-heading font-bold text-[#C86D51]">38</span>
               <p className="text-xs text-stone-500 font-medium mt-2">Marcenarias, Marmorarias, Iluminação</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-stone-300">
               <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest block mb-1">Medições de Obra Mês</span>
               <span className="text-3xl font-heading font-bold text-[#1A1A1A]">R$ 1.12M</span>
               <p className="text-xs text-stone-500 font-medium mt-2">Aprovadas pelo Cliente</p>
            </div>
         </div>

         {/* Tabela de Cronograma & Especificações - STRICT NO SHADOWS */}
         <div className="bg-white rounded-xl border border-stone-300 overflow-hidden">
            <div className="p-6 border-b border-stone-200 flex justify-between items-center bg-stone-50">
               <h3 className="font-heading font-bold text-lg text-[#1A1A1A]">Controle de Cronograma Executivo de Projetos</h3>
               <span className="text-xs font-bold text-stone-500">Filtrado por: Todos os Estúdios</span>
            </div>

            <div className="overflow-x-auto">
               <table className="w-full text-left text-xs">
                  <thead className="bg-stone-100 border-b border-stone-200 text-stone-600 font-bold uppercase tracking-wider">
                     <tr>
                        <th className="p-4">Projeto / Tipologia</th>
                        <th className="p-4">Localização / Metragem</th>
                        <th className="p-4">Fase Atual da Obra</th>
                        <th className="p-4">Status de Fornecedores</th>
                        <th className="p-4">Ação</th>
                     </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200">
                     {arquiteturaProjetos.map((proj) => (
                        <tr key={proj.id} className="hover:bg-stone-50 transition-colors">
                           <td className="p-4">
                              <div className="flex items-center gap-3">
                                 <img src={proj.imageAfter} className="w-12 h-9 rounded object-cover border border-stone-300" alt={proj.title} />
                                 <div>
                                    <span className="font-bold text-[#1A1A1A] block text-sm">{proj.title}</span>
                                    <span className="text-[10px] text-stone-500 uppercase">{proj.category}</span>
                                 </div>
                              </div>
                           </td>
                           <td className="p-4 font-bold text-stone-700">{proj.location}<span className="block text-[10px] text-stone-400 font-normal">{proj.area}</span></td>
                           <td className="p-4 font-bold text-[#C86D51]">Fase 2: Marcenaria & Marmoraria</td>
                           <td className="p-4">
                              <span className="bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 rounded text-[10px] font-bold">
                                 ✓ Espec. 100% Aprovada
                              </span>
                           </td>
                           <td className="p-4">
                              <button className="bg-[#1A1A1A] text-white hover:bg-stone-800 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-colors border border-stone-800">
                                 Abrir Prancha
                              </button>
                           </td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
         </div>

      </main>

    </div>
  );
};
